import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import NavigationOutlinedIcon from "@mui/icons-material/NavigationOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import SMQMainContainer from "../../../components/SMQMainContainer";
import guideStyles from "./GuideUtilisateur.styles";
import { GUIDE_META, GUIDE_SECTIONS } from "./guideContent";

const SECTION_ICONS = {
  analysis: AnalyticsOutlinedIcon,
  communication: CampaignOutlinedIcon,
  dashboard: DashboardOutlinedIcon,
  edit: EditNoteOutlinedIcon,
  export: AssessmentOutlinedIcon,
  faq: HelpOutlineOutlinedIcon,
  matrix: FactCheckOutlinedIcon,
  navigation: NavigationOutlinedIcon,
  overview: MenuBookOutlinedIcon,
  register: AssignmentOutlinedIcon,
  save: SaveOutlinedIcon,
};

const CALLOUT_ICONS = {
  info: InfoOutlinedIcon,
  tip: TipsAndUpdatesOutlinedIcon,
  warning: WarningAmberOutlinedIcon,
};

function GuideTable({ table }) {
  return (
    <Box>
      <Typography component="h3" sx={guideStyles.subTitle}>
        {table.title}
      </Typography>
      <TableContainer component={Paper} sx={guideStyles.tableWrap}>
        <Table aria-label={table.title} size="small" sx={guideStyles.table}>
          <TableHead>
            <TableRow>
              {table.headers.map((header) => (
                <TableCell key={header}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row, rowIndex) => (
              <TableRow key={`${table.title}-${rowIndex + 1}`}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={`${rowIndex + 1}-${cellIndex + 1}`}>
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

GuideTable.propTypes = {
  table: PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

function GuideCallout({ callout }) {
  const Icon = CALLOUT_ICONS[callout.tone] || InfoOutlinedIcon;
  const toneStyle =
    callout.tone === "warning"
      ? guideStyles.calloutWarning
      : callout.tone === "tip"
        ? guideStyles.calloutTip
        : guideStyles.calloutInfo;

  return (
    <Box role="note" sx={{ ...guideStyles.callout, ...toneStyle }}>
      <Icon aria-hidden="true" fontSize="small" />
      <Box>
        <Typography component="h3" sx={guideStyles.calloutTitle}>
          {callout.title}
        </Typography>
        <Typography component="p" sx={{ ...guideStyles.bodyText, color: "inherit" }}>
          {callout.text}
        </Typography>
      </Box>
    </Box>
  );
}

GuideCallout.propTypes = {
  callout: PropTypes.shape({
    text: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    tone: PropTypes.oneOf(["info", "tip", "warning"]),
  }).isRequired,
};

function TableOfContents({ activeSection, onNavigate }) {
  return (
    <Box component="ul" sx={guideStyles.tocList}>
      {GUIDE_SECTIONS.map((section) => (
        <Box component="li" key={section.id}>
          <Box
            aria-current={activeSection === section.id ? "location" : undefined}
            component="button"
            onClick={() => onNavigate(section.id)}
            sx={{
              ...guideStyles.tocLink,
              ...(activeSection === section.id ? guideStyles.tocLinkActive : {}),
            }}
            type="button"
          >
            <Box component="span" sx={guideStyles.tocNumber}>
              {section.number}
            </Box>
            <Box component="span">{section.title}</Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

TableOfContents.propTypes = {
  activeSection: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

function GuideSection({ section }) {
  const Icon = SECTION_ICONS[section.icon] || MenuBookOutlinedIcon;

  return (
    <Paper
      aria-labelledby={`${section.id}-title`}
      component="section"
      id={section.id}
      tabIndex={-1}
      sx={guideStyles.section}
    >
      <Box sx={guideStyles.sectionHeader}>
        <Box aria-hidden="true" sx={guideStyles.sectionNumber}>
          {section.number}
        </Box>
        <Typography component="h2" id={`${section.id}-title`} sx={guideStyles.sectionTitle}>
          <Icon aria-hidden="true" sx={guideStyles.sectionIcon} />
          {section.title}
        </Typography>
      </Box>

      <Box sx={guideStyles.sectionBody}>
        <Typography component="p" sx={guideStyles.introduction}>
          {section.introduction}
        </Typography>

        {section.cards?.length > 0 && (
          <Box sx={guideStyles.cardsGrid}>
            {section.cards.map((card) => (
              <Box key={card.title} sx={guideStyles.infoCard}>
                <Typography component="h3" sx={guideStyles.infoCardTitle}>
                  {card.title}
                </Typography>
                <Typography component="p" sx={guideStyles.bodyText}>
                  {card.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {section.steps?.length > 0 && (
          <Box aria-label={`Étapes — ${section.title}`} sx={guideStyles.steps}>
            {section.steps.map((step, index) => (
              <Box key={step.title} sx={guideStyles.step}>
                <Box aria-hidden="true" sx={guideStyles.stepNumber}>
                  {index + 1}
                </Box>
                <Box sx={guideStyles.stepContent}>
                  <Typography component="h3" sx={guideStyles.infoCardTitle}>
                    {step.title}
                  </Typography>
                  <Typography component="p" sx={guideStyles.bodyText}>
                    {step.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {section.tables?.map((table) => (
          <GuideTable key={table.title} table={table} />
        ))}

        {section.bulletGroups?.map((group) => (
          <Box key={group.title}>
            <Typography component="h3" sx={guideStyles.subTitle}>
              {group.title}
            </Typography>
            <Box component="ul" sx={guideStyles.bulletList}>
              {group.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Box>
          </Box>
        ))}

        {section.faqs?.length > 0 && (
          <Box sx={guideStyles.faqList}>
            {section.faqs.map((faq, faqIndex) => {
              const contentId = `${section.id}-faq-${faqIndex + 1}-content`;
              const headerId = `${section.id}-faq-${faqIndex + 1}-header`;

              return (
              <Accordion disableGutters key={faq.question} sx={guideStyles.faqItem}>
                <AccordionSummary
                  aria-controls={contentId}
                  expandIcon={<ExpandMoreIcon />}
                  id={headerId}
                >
                  <Typography component="h3" sx={guideStyles.faqQuestion}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails aria-labelledby={headerId} id={contentId}>
                  <Typography component="p" sx={guideStyles.bodyText}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
              );
            })}
          </Box>
        )}

        {section.callouts?.map((callout) => (
          <GuideCallout callout={callout} key={callout.title} />
        ))}
      </Box>
    </Paper>
  );
}

GuideSection.propTypes = {
  section: PropTypes.shape({
    bulletGroups: PropTypes.array,
    callouts: PropTypes.array,
    cards: PropTypes.array,
    faqs: PropTypes.array,
    icon: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    introduction: PropTypes.string.isRequired,
    number: PropTypes.string.isRequired,
    steps: PropTypes.array,
    tables: PropTypes.array,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

function GuideUtilisateur() {
  const initialSection = useMemo(() => {
    if (typeof window === "undefined") return GUIDE_SECTIONS[0].id;
    const hash = window.location.hash.replace("#", "");
    return GUIDE_SECTIONS.some((section) => section.id === hash)
      ? hash
      : GUIDE_SECTIONS[0].id;
  }, []);
  const [activeSection, setActiveSection] = useState(initialSection);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const initialHashHandledRef = useRef(false);

  const navigateToSection = useCallback((sectionId) => {
    const sectionElement = document.getElementById(sectionId);
    if (!sectionElement) return;

    setActiveSection(sectionId);
    setMobileTocOpen(false);
    window.history.replaceState(null, "", `#${sectionId}`);
    sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    sectionElement.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (initialHashHandledRef.current || typeof window === "undefined") return;
    initialHashHandledRef.current = true;

    if (window.location.hash) {
      window.requestAnimationFrame(() => {
        document.getElementById(initialSection)?.scrollIntoView({ block: "start" });
      });
    }
  }, [initialSection]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => second.intersectionRatio - first.intersectionRatio)[0];

        if (visibleEntry?.target?.id) {
          setActiveSection(visibleEntry.target.id);
        }
      },
      { rootMargin: "-18% 0px -68% 0px", threshold: [0, 0.2, 0.5, 1] }
    );

    GUIDE_SECTIONS.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <SMQMainContainer title="Guide utilisateur SMQ / NC">
      <Box sx={guideStyles.page}>
        <Paper component="header" sx={guideStyles.hero}>
          {/* <Box aria-hidden="true" sx={guideStyles.heroIcon}>
            <MenuBookOutlinedIcon fontSize="large" />
          </Box> */}
          <Typography component="p" sx={guideStyles.heroEyebrow}>
            {GUIDE_META.eyebrow}
          </Typography>
          <Typography component="h1" sx={guideStyles.heroTitle}>
            {GUIDE_META.title}
          </Typography>
          <Typography component="p" sx={guideStyles.heroDescription}>
            {GUIDE_META.description}
          </Typography>
          <Chip label={GUIDE_META.standard} size="small" sx={guideStyles.standardChip} variant="outlined" />
        </Paper>

        <Accordion
          disableGutters
          expanded={mobileTocOpen}
          onChange={(_, expanded) => setMobileTocOpen(expanded)}
          sx={guideStyles.mobileToc}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={guideStyles.tocTitle}>
              <AccountTreeOutlinedIcon aria-hidden="true" fontSize="small" />
              Table des matières
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableOfContents activeSection={activeSection} onNavigate={navigateToSection} />
          </AccordionDetails>
        </Accordion>

        <Box sx={guideStyles.layout}>
          <Box component="nav" aria-label="Table des matières du guide" sx={guideStyles.desktopToc}>
            <Box sx={guideStyles.tocTitle}>
              <AccountTreeOutlinedIcon aria-hidden="true" fontSize="small" />
              Table des matières
            </Box>
            <TableOfContents activeSection={activeSection} onNavigate={navigateToSection} />
          </Box>

          <Box sx={guideStyles.content}>
            {GUIDE_SECTIONS.map((section) => (
              <GuideSection key={section.id} section={section} />
            ))}
            <Typography component="p" sx={guideStyles.footer}>
              Guide utilisateur — Module Non-Conformité / SMQ — FID
            </Typography>
          </Box>
        </Box>
      </Box>
    </SMQMainContainer>
  );
}

export default GuideUtilisateur;
